import { useMemo } from 'react';
import {
  CalendarMonthWeekGrid,
  type CalendarDayRenderProps,
} from '~/features/shared/calendar/CalendarMonthWeekGrid';
import { parseISODate, type CalendarViewMode } from '~/features/shared/calendar/dateUtils';
import { CONTRACT_START_STYLES, STATUS_LABEL, STATUS_STYLES } from '../constants';
import { contractStartLabel } from '../contractMarkers';
import { timestampToLocalClock, timezoneAbbreviation } from '../dateTime';
import type { AvailabilityDay, CalendarContract } from '../types';

const NO_CONTRACTS: ReadonlyMap<string, CalendarContract[]> = new Map();

interface AvailabilityCalendarGridProps {
  anchor: Date;
  /**
   * [MCV-1821] Contracts to mark on their start date, already filtered to the current scope.
   * Each is shown as a plain text line in its day cell; it is not interactive.
   */
  contractsByDate?: ReadonlyMap<string, CalendarContract[]>;
  daysByDate: ReadonlyMap<string, AvailabilityDay>;
  /**
   * Disables every day, not just past ones. Set while the calendar has no
   * authoritative load: the days it is showing are an empty projection, so
   * selecting one would open an editor over rows that may merely be hidden.
   */
  disabled?: boolean;
  onDateClick: (date: string, shiftKey: boolean) => void;
  selectedDates: string[];
  todayISO: string;
  viewMode: CalendarViewMode;
  visibleDates: Date[];
}

const toISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const entryLabel = (entry: AvailabilityDay['entries'][number]): string => {
  if (entry.detailMode === 'DATE_ONLY') return STATUS_LABEL[entry.status];
  const shift = entry.shiftLabel ? entry.shiftLabel.toLowerCase() : 'shift';
  const type = entry.shiftType === 'CALL' ? 'call' : 'active';
  let times = '';
  if (entry.startsAt && entry.endsAt) {
    const start = timestampToLocalClock(entry.startsAt, entry.timezone);
    const end = timestampToLocalClock(entry.endsAt, entry.timezone);
    if (start && end) {
      times = ` ${start}–${end} ${timezoneAbbreviation(entry.startsAt, entry.timezone)}`;
    }
  }
  return `${STATUS_LABEL[entry.status]}: ${shift} ${type}${times}`;
};

export const AvailabilityCalendarGrid = ({
  anchor,
  contractsByDate = NO_CONTRACTS,
  daysByDate,
  disabled: allDisabled = false,
  onDateClick,
  selectedDates,
  todayISO,
  viewMode,
  visibleDates,
}: AvailabilityCalendarGridProps) => {
  const selected = useMemo(() => new Set(selectedDates), [selectedDates]);
  const disabled = useMemo(
    () =>
      new Set(
        visibleDates.filter((date) => allDisabled || date < parseISODate(todayISO)).map(toISO)
      ),
    [allDisabled, todayISO, visibleDates]
  );

  const ariaLabel = ({ date, isoDate }: CalendarDayRenderProps) => {
    const entries = daysByDate.get(isoDate)?.entries ?? [];
    const summaries = entries.map(entryLabel);
    const contractStarts = (contractsByDate.get(isoDate) ?? []).map(contractStartLabel);
    const locked = entries.some((entry) => entry.status === 'HELD' || entry.status === 'BOOKED');
    const past = isoDate < todayISO;
    return [
      date.toLocaleDateString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
      selected.has(isoDate) ? 'selected' : 'not selected',
      summaries.length ? summaries.join(', ') : 'no availability',
      contractStarts.length ? contractStarts.join(', ') : null,
      locked ? 'contains locked commitments' : null,
      past ? 'past date; editing disabled' : null,
      allDisabled && !past ? 'editing disabled' : null,
    ]
      .filter(Boolean)
      .join('; ');
  };

  return (
    <CalendarMonthWeekGrid
      anchor={anchor}
      dataCyDayPrefix="provider-calendar-day"
      dates={visibleDates}
      disabledDates={disabled}
      getDateAriaLabel={ariaLabel}
      onDateClick={(date, options) => onDateClick(date, options.shiftKey)}
      renderDay={({ isoDate, viewMode: mode }) => {
        const entries = daysByDate.get(isoDate)?.entries ?? [];
        const contracts = contractsByDate.get(isoDate) ?? [];
        const maxVisible = mode === 'month' ? 3 : 10;
        return (
          <div className="mt-1 flex flex-col gap-1" aria-hidden="true">
            {entries.slice(0, maxVisible).map((entry) => (
              <span
                className={`truncate rounded px-1.5 py-0.5 text-[11px] font-medium ${STATUS_STYLES[entry.status]}`}
                data-cy={`provider-calendar-entry-${entry.id}`}
                key={entry.id}
              >
                {entryLabel(entry)}
              </span>
            ))}
            {entries.length > maxVisible ? (
              <span className="px-1 text-[11px] font-semibold text-primary-50">
                +{entries.length - maxVisible} more
              </span>
            ) : null}
            {contracts.map((contract) => (
              <span
                aria-label={contractStartLabel(contract)}
                className={`truncate rounded px-1.5 py-0.5 text-[11px] font-medium ${CONTRACT_START_STYLES}`}
                data-cy={`provider-calendar-contract-${contract.contractId}`}
                key={contract.contractId}
                title={contractStartLabel(contract)}
              >
                {contractStartLabel(contract)}
              </span>
            ))}
          </div>
        );
      }}
      selectedDates={selected}
      today={parseISODate(todayISO)}
      viewMode={viewMode}
    />
  );
};
